import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const DECLARATIONS = [
  "Any civil proceedings, or litigation, including judgement debts or arbitration?",
  "Any bankruptcy proceedings or proceedings for the sequestration of your estate?",
  "Any such debts still outstanding, whether in full or in part?",
  "Failed to satisfy any such debts within one year of the making of the order?",
  "Been refused entry to any profession or vocation, or been dismissed or requested to resign from any office or employment?",
  "Been refused, or restricted in, the right to carry on any trade, business or profession?",
  "Been disqualified by a court from acting as a director of a company?",
  "Been refused, had revoked, restricted or terminated any licence, authorisation, registration or membership?",
  "Been criticised, censured, disciplined, suspended, expelled, fined, or been the subject of any other disciplinary action?",
  "Resigned, or been asked to resign, from any regulatory body?",
  "Been the subject of any civil action which has resulted in a finding against you by a court?",
  "Knowingly been the subject of an investigation into allegations of misconduct or malpractice?",
  "Been put into liquidation, had a petition presented, or been wound up?",
  "Ceased trading in circumstances in which one or more creditors did not receive full payment?",
  "Been adjudged by a court liable for any fraud, misfeasance, wrongful trading or other misconduct?",
  "Been investigated by an inspector appointed under companies or any other legislation?"
];

const API = process.env.REACT_APP_API_URL;
const inp = (extra={}) => ({ width:'100%', padding:'10px 12px', borderRadius:8, border:'1px solid #d1d5db', fontSize:14, boxSizing:'border-box', marginBottom:12, ...extra });

export default function AgreementPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite,       setInvite]       = useState(null);
  const [template,     setTemplate]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [submitting,   setSubmitting]   = useState(false);
  const [done,         setDone]         = useState(false);
  const [idSkipped,    setIdSkipped]    = useState(false);

  const [dynamicValues, setDynamicValues] = useState({});
  const [dynamicErrors, setDynamicErrors] = useState({});

  const [intro,        setIntro]        = useState({ name:'', dob:'', address:'', phone:'', mobile:'', email:'' });
  const [firm,         setFirm]         = useState({ firm_name:'', firm_address:'', phone:'', nature_of_business:'' });
  const [declarations, setDeclarations] = useState(Array(DECLARATIONS.length).fill(null));
  const [declarationDetails, setDeclarationDetails] = useState('');

  const [esignName,    setEsignName]    = useState('');
  const [drivingLicence, setDrivingLicence] = useState(null);
  const [passport,     setPassport]     = useState(null);
  const [section,      setSection]      = useState(1);

  useEffect(() => {
    const load = async () => {
      try {
        const res  = await fetch(`${API}/api/invites/${token}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Invalid link');
        setInvite(data);
        setIntro(i => ({ ...i, email: data.email }));

        // Restore any saved draft for this invite (progress is saved on-device)
        let draft = null;
        try { draft = JSON.parse(localStorage.getItem(`agreement_draft_${token}`) || 'null'); } catch { /* ignore */ }

        if (data.template_id) {
          const tres  = await fetch(`${API}/api/agreement-templates/by-id/${data.template_id}`);
          const tdata = await tres.json();
          if (tdata && tdata.extracted_fields) {
            setTemplate(tdata);
            const defaults = {};
            tdata.extracted_fields.forEach(f => {
              if (f.type === 'legal_text') return;
              if (f.type === 'email' && data.email) defaults[f.key] = data.email;
              else defaults[f.key] = f.type === 'checkbox' ? false : '';
            });
            setDynamicValues(draft?.dynamicValues ? { ...defaults, ...draft.dynamicValues } : defaults);
          } else {
            setTemplate(false);
          }
        } else {
          setTemplate(false);
        }

        if (draft) {
          if (draft.intro)        setIntro(i => ({ ...i, ...draft.intro }));
          if (draft.firm)         setFirm(f => ({ ...f, ...draft.firm }));
          if (Array.isArray(draft.declarations) && draft.declarations.length === DECLARATIONS.length) setDeclarations(draft.declarations);
          if (draft.declarationDetails) setDeclarationDetails(draft.declarationDetails);
          if (draft.section > 1)  setSection(draft.section);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  // Auto-save draft so an interrupted introducer never loses progress
  useEffect(() => {
    if (loading || done) return;
    try {
      localStorage.setItem(`agreement_draft_${token}`, JSON.stringify({
        section, intro, firm, declarations, declarationDetails, dynamicValues,
      }));
    } catch { /* storage full / private mode — fail silently */ }
  }, [section, intro, firm, declarations, declarationDetails, dynamicValues, loading, done, token]);

  const validateDynamic = () => {
    if (!template) return true;
    const errs = {};
    template.extracted_fields.forEach(f => {
      if (f.type === 'legal_text') return;
      const v = dynamicValues[f.key];
      if (f.required) {
        if (f.type === 'select' && (!v || v === '')) errs[f.key] = 'Required';
        else if (f.type === 'checkbox' && !v) errs[f.key] = 'Required';
        else if (typeof v === 'string' && !v.trim()) errs[f.key] = 'Required';
      }
    });
    setDynamicErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!esignName.trim()) return setError('Please type your full name to sign');
    setSubmitting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('token', token);
      fd.append('esign_name', esignName);
      fd.append('id_upload_skipped', idSkipped.toString());

      if (template) {
        fd.append('template_id', template.id);
        fd.append('dynamic_form_data', JSON.stringify(dynamicValues));
      } else {
        fd.append('introducer_details', JSON.stringify(intro));
        fd.append('firm_details', JSON.stringify(firm));
        fd.append('declaration_answers', JSON.stringify(
          declarations.map((ans, i) => ({ question: DECLARATIONS[i], answer: ans, details: declarationDetails }))
        ));
      }

      if (drivingLicence) fd.append('driving_licence', drivingLicence);
      if (passport)       fd.append('passport', passport);

      const res  = await fetch(`${API}/api/agreements/submit`, { method:'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      try { localStorage.removeItem(`agreement_draft_${token}`); } catch { /* ignore */ }
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={styles.center}>Loading agreement...</div>;
  if (error && !invite) return <div style={styles.center}><p style={styles.error}>{error}</p></div>;
  if (done) return (
    <div style={styles.center}>
      <div style={{ background:'#fff', borderRadius:16, padding:'40px 36px', maxWidth:480, width:'100%', boxShadow:'0 4px 24px rgba(0,0,0,0.08)', textAlign:'center' }}>
        <div style={{ width:72, height:72, borderRadius:'50%', background:'#d1fae5', display:'flex', alignItems:'center', justifyContent:'center', fontSize:34, margin:'0 auto 20px' }}>🎉</div>
        <h2 style={{ color:'#0e1b2c', margin:'0 0 8px', fontSize:22 }}>Agreement signed — you're nearly there</h2>
        <p style={{ color:'#4b5a6e', fontSize:14, lineHeight:1.7, margin:'0 0 20px' }}>
          Your broker has been notified and will review your application — most are approved within
          <strong> 1 business day</strong>. We'll email you the moment you're approved.
        </p>
        <button onClick={() => navigate('/login')} style={{ padding:'12px 26px', borderRadius:8, border:'none', background:'#10b981', color:'#fff', fontSize:15, fontWeight:600, cursor:'pointer' }}>
          Sign in to track your application →
        </button>
      </div>
    </div>
  );

  const totalSections = template ? 3 : 5;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Introducer Agreement</h2>
          {invite && <p style={styles.sub}>Welcome{invite.introducer_name ? `, ${invite.introducer_name.split(' ')[0]}` : ''} — this takes about 5 minutes, and your progress is saved automatically on this device.</p>}
        </div>

        <div style={styles.steps}>
          {(template ? ['Your details', 'ID', 'Sign'] : ['Personal', 'Firm', 'Declarations', 'ID', 'Sign']).map((label, i) => (
            <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, minWidth:0 }}>
              <div style={{ ...styles.step, ...(section === i+1 ? styles.stepActive : section > i+1 ? styles.stepDone : {}) }}>
                {section > i+1 ? '✓' : i+1}
              </div>
              <span style={{ fontSize:10.5, fontWeight: section === i+1 ? 700 : 500, color: section === i+1 ? '#10b981' : '#a0aec0', whiteSpace:'nowrap' }}>{label}</span>
            </div>
          ))}
        </div>

        {/* CUSTOM TEMPLATE FLOW */}
        {template && (
          <>
            {section === 1 && (
              <DynamicFormSection
                template={template}
                values={dynamicValues}
                errors={dynamicErrors}
                onChange={(key, val) => {
                  setDynamicValues(p => ({ ...p, [key]: val }));
                  if (dynamicErrors[key]) setDynamicErrors(p => { const e={...p}; delete e[key]; return e; });
                }}
                onNext={() => { if (validateDynamic()) { setError(''); setSection(2); } else { setError('Please answer all required questions before continuing.'); } }}
              />
            )}
            {section === 2 && (
              <IDSection
                drivingLicence={drivingLicence} setDrivingLicence={setDrivingLicence}
                passport={passport} setPassport={setPassport}
                idSkipped={idSkipped} setIdSkipped={setIdSkipped}
                onBack={() => setSection(1)} onNext={() => setSection(3)}
              />
            )}
            {section === 3 && (
              <SignSection
                esignName={esignName} setEsignName={setEsignName}
                invite={invite} error={error}
                submitting={submitting} onBack={() => setSection(2)}
                onSubmit={handleSubmit}
                declarationTexts={
                  template?.extracted_fields
                    ?.filter(f => f.type === 'legal_text' && (f.section || '').toUpperCase() === 'DECLARATION')
                    ?.map(f => f.text || f.label) || []
                }
                agreementUrl={template?.id ? `${API}/api/agreement-templates/canonical/${template.id}` : null}
                agreementName={template?.display_name}
              />
            )}
          </>
        )}

        {/* HARDCODED FALLBACK FLOW */}
        {template === false && (
          <>
            {section === 1 && (
              <div>
                <h3 style={styles.sectionTitle}>Your Personal Details</h3>
                {[['name','Full Name'],['dob','Date of Birth'],['address','Home Address'],['phone','Telephone'],['mobile','Mobile'],['email','Email']].map(([k,l]) => (
                  <div key={k}>
                    <label style={styles.label}>{l}</label>
                    <input style={inp()} value={intro[k]} onChange={e => setIntro(p=>({...p,[k]:e.target.value}))} type={k==='dob'?'date':k==='email'?'email':'text'} />
                  </div>
                ))}
                <div style={styles.buttons}><button onClick={() => setSection(2)} style={styles.submit}>Next →</button></div>
              </div>
            )}
            {section === 2 && (
              <div>
                <h3 style={styles.sectionTitle}>Introducing Firm Details</h3>
                {[['firm_name','Firm Name'],['firm_address','Firm Address'],['phone','Telephone'],['nature_of_business','Nature of Business']].map(([k,l]) => (
                  <div key={k}>
                    <label style={styles.label}>{l}</label>
                    <input style={inp()} value={firm[k]} onChange={e => setFirm(p=>({...p,[k]:e.target.value}))} />
                  </div>
                ))}
                <div style={styles.buttons}>
                  <button onClick={() => setSection(1)} style={styles.cancel}>← Back</button>
                  <button onClick={() => setSection(3)} style={styles.submit}>Next →</button>
                </div>
              </div>
            )}
            {section === 3 && (
              <div>
                <h3 style={styles.sectionTitle}>Statement of Honesty & Integrity</h3>
                <p style={styles.sub}>Please answer Yes or No to each question.</p>
                {DECLARATIONS.map((q, i) => (
                  <div key={i} style={{ marginBottom:10, padding:'12px 14px', background:'#fff', border:'1px solid #e5e7eb', borderRadius:8 }}>
                    <p style={{ margin:'0 0 8px', fontSize:13, color:'#1a2e4a', fontWeight:500 }}>{q}</p>
                    <div style={{ display:'flex', gap:8 }}>
                      {['Yes','No'].map(ans => (
                        <button key={ans} onClick={() => setDeclarations(d => { const n=[...d]; n[i]=ans; return n; })}
                          style={{ padding:'6px 20px', borderRadius:6, border:'2px solid',
                            borderColor: declarations[i]===ans ? (ans==='Yes'?'#dc2626':'#10b981') : '#d1d5db',
                            background: declarations[i]===ans ? (ans==='Yes'?'#fef2f2':'#f0fdf4') : '#f9fafb',
                            color: declarations[i]===ans ? (ans==='Yes'?'#dc2626':'#10b981') : '#6b7280',
                            cursor:'pointer', fontSize:13, fontWeight: declarations[i]===ans ? 700 : 500 }}>
                          {ans}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {declarations.some(d => d==='Yes') && (
                  <div>
                    <label style={styles.label}>Please provide details</label>
                    <textarea rows={4} style={inp()} value={declarationDetails} onChange={e => setDeclarationDetails(e.target.value)} />
                  </div>
                )}
                <div style={styles.buttons}>
                  <button onClick={() => setSection(2)} style={styles.cancel}>← Back</button>
                  <button onClick={() => setSection(4)} style={styles.submit}>Next →</button>
                </div>
              </div>
            )}
            {section === 4 && (
              <IDSection
                drivingLicence={drivingLicence} setDrivingLicence={setDrivingLicence}
                passport={passport} setPassport={setPassport}
                idSkipped={idSkipped} setIdSkipped={setIdSkipped}
                onBack={() => setSection(3)} onNext={() => setSection(5)}
              />
            )}
            {section === 5 && (
              <SignSection
                esignName={esignName} setEsignName={setEsignName}
                invite={invite} error={error}
                submitting={submitting} onBack={() => setSection(4)}
                onSubmit={handleSubmit}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Dynamic form section ──────────────────────────────────────────────────────
// ── Agreement document viewer ─────────────────────────────────────────────────
// Embeds the broker's actual agreement PDF (the canonical document the
// introducer is signing) so they read the real terms, not a summary.
function AgreementViewer({ agreementUrl, displayName }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!agreementUrl) return null;
  return (
    <div style={{ marginBottom:20, border:'1px solid #cbd5e1', borderRadius:10, overflow:'hidden' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
        gap:10, padding:'12px 14px', background:'#f1f5f9' }}>
        <div style={{ minWidth:0 }}>
          <p style={{ fontSize:13, fontWeight:700, color:'#1a2e4a', margin:0 }}>
            📄 {displayName || 'Your Introducer Agreement'}
          </p>
          <p style={{ fontSize:11.5, color:'#64748b', margin:'2px 0 0' }}>
            This is the full agreement you'll be signing — please read it.
          </p>
        </div>
        <div style={{ display:'flex', gap:8, flexShrink:0 }}>
          <button type="button" onClick={() => setExpanded(e => !e)}
            style={{ padding:'7px 12px', borderRadius:8, border:'1px solid #cbd5e1',
              background:'#fff', color:'#1a2e4a', fontSize:12, fontWeight:600, cursor:'pointer' }}>
            {expanded ? 'Hide' : 'Read here'}
          </button>
          <a href={agreementUrl} target="_blank" rel="noopener noreferrer"
            style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#1a2e4a',
              color:'#fff', fontSize:12, fontWeight:600, textDecoration:'none', whiteSpace:'nowrap' }}>
            Open PDF ↗
          </a>
        </div>
      </div>
      {expanded && (
        <iframe title="Introducer agreement" src={agreementUrl}
          style={{ width:'100%', height:420, border:'none', borderTop:'1px solid #e2e8f0', display:'block' }} />
      )}
    </div>
  );
}

function DynamicFormSection({ template, values, errors, onChange, onNext }) {
  // Group fields by section, preserving order
  const sections = {};
  const sectionOrder = [];
  template.extracted_fields.forEach(f => {
    const s = f.section || 'Your Details';
    if (!sections[s]) { sections[s] = []; sectionOrder.push(s); }
    sections[s].push(f);
  });

  return (
    <div>
      <h3 style={styles.sectionTitle}>{template.display_name || 'Agreement Details'}</h3>
      <p style={{ fontSize:13, color:'#718096', marginBottom:20 }}>
        Please complete all fields below. This forms part of your introducer agreement.
      </p>
      <AgreementViewer
        agreementUrl={template?.id ? `${API}/api/agreement-templates/canonical/${template.id}` : null}
        displayName={template.display_name}
      />
      {sectionOrder.map(sectionName => (
        <div key={sectionName} style={{ marginBottom:24 }}>
          <p style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em',
            color:'#64748b', margin:'0 0 10px', paddingBottom:6, borderBottom:'2px solid #e2e8f0' }}>
            {sectionName}
          </p>
          {sections[sectionName].map(f => (
            <FieldInput key={f.key} field={f} value={values[f.key]} error={errors[f.key]} onChange={v => onChange(f.key, v)} />
          ))}
        </div>
      ))}
      <div style={styles.buttons}>
        <button onClick={onNext} style={styles.submit}>Next →</button>
      </div>
    </div>
  );
}

// ── Toggle switch (mirrors the slider shown in the template Preview modal) ─────
function Toggle({ id, checked, onChange, label, required, error }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
      padding:'10px 14px', borderRadius:10,
      border: error ? '1px solid #fca5a5' : '1px solid #e5e7eb',
      background: error ? '#fef2f2' : '#fff' }}>
      <span style={{ fontSize:13, color:'#374151', flex:1 }}>
        {label}{required && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}
      </span>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        style={{
          position:'relative', width:44, height:24, borderRadius:12, border:'none',
          background: checked ? '#10b981' : '#d1d5db',
          cursor:'pointer', flexShrink:0, transition:'background 0.2s',
          outline:'none', marginLeft:10,
        }}
      >
        <span style={{
          position:'absolute', top:3, left: checked ? 23 : 3,
          width:18, height:18, borderRadius:'50%', background:'#fff',
          transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </button>
      <span style={{ marginLeft:10, fontSize:12, fontWeight:600,
        color: checked ? '#10b981' : '#9ca3af', minWidth:22, textAlign:'right' }}>
        {checked ? 'Yes' : 'No'}
      </span>
    </div>
  );
}

// ── Field input ───────────────────────────────────────────────────────────────
function FieldInput({ field: f, value, error, onChange }) {
  const base  = inp({ borderColor: error ? '#ef4444':'#d1d5db', background: error ? '#fef2f2':'#fff' });
  const label = <label style={styles.label}>{f.label}{f.required && <span style={{color:'#ef4444',marginLeft:3}}>*</span>}</label>;

  // Legal text — read only display
  if (f.type === 'legal_text') {
    const text = f.text || f.label || '';
    const isSubHeading = text.length < 120;
    if (isSubHeading) return (
      <div style={{ margin:'14px 0 6px', padding:'8px 12px', background:'#f1f5f9',
        borderRadius:6, borderLeft:'3px solid #64748b' }}>
        <p style={{ fontSize:12, color:'#475569', margin:0, lineHeight:1.5, fontStyle:'italic' }}>{text}</p>
      </div>
    );
    return (
      <div style={{ marginBottom:8, padding:'12px 14px', background:'#f8fafc',
        borderLeft:'3px solid #94a3b8', borderRadius:'0 6px 6px 0' }}>
        <p style={{ fontSize:12.5, color:'#374151', margin:0, lineHeight:1.6 }}>{text}</p>
      </div>
    );
  }

  // Yes/No question — card style
  if (f.type === 'select' && f.options?.length === 2 && f.options.includes('Yes') && f.options.includes('No')) return (
    <div style={{ marginBottom:8, padding:'12px 14px', background:'#fff',
      border:'1px solid #e5e7eb', borderRadius:8, boxShadow:'0 1px 2px rgba(0,0,0,0.04)' }}>
      <p style={{ fontSize:13, color:'#1a2e4a', margin:'0 0 10px', lineHeight:1.6, fontWeight:500 }}>
        {f.label}{f.required && <span style={{color:'#ef4444',marginLeft:3}}>*</span>}
      </p>
      <div style={{ display:'flex', gap:8 }}>
        {['Yes','No'].map(opt => (
          <button key={opt} type="button" onClick={() => onChange(opt)}
            style={{ padding:'7px 24px', borderRadius:8, border:'2px solid',
              borderColor: value===opt ? (opt==='Yes' ? '#dc2626' : '#10b981') : '#d1d5db',
              background:  value===opt ? (opt==='Yes' ? '#fef2f2' : '#f0fdf4') : '#f9fafb',
              color:       value===opt ? (opt==='Yes' ? '#dc2626' : '#10b981') : '#6b7280',
              cursor:'pointer', fontSize:13, fontWeight: value===opt ? 700 : 500 }}>
            {opt}
          </button>
        ))}
      </div>
      {error && <p style={styles.fieldError}>{error}</p>}
    </div>
  );

  // Rendered as a slider toggle, matching the "Preview" modal on the
  // broker's Agreement Templates page (AgreementTemplateManager.js) — that
  // preview previously showed a toggle here while this real invite flow
  // showed a plain tick box for the same field. Kept visually identical to
  // that preview's toggle (and to the unused DynamicAgreementForm.js one).
  if (f.type === 'checkbox') return (
    <div style={{ marginBottom:12 }}>
      <Toggle
        id={f.key}
        checked={!!value}
        onChange={onChange}
        label={f.label}
        required={f.required}
        error={error}
      />
      {error && <p style={styles.fieldError}>{error}</p>}
    </div>
  );

  if (f.type === 'textarea') return (
    <div>
      {label}
      <textarea rows={3} style={base} value={value||''} onChange={e => onChange(e.target.value)} placeholder={f.placeholder} />
      {error && <p style={styles.fieldError}>{error}</p>}
    </div>
  );

  if (f.type === 'select') return (
    <div>
      {label}
      <select style={base} value={value||''} onChange={e => onChange(e.target.value)}>
        <option value="">Select…</option>
        {(f.options||[]).map(o => <option key={o}>{o}</option>)}
      </select>
      {error && <p style={styles.fieldError}>{error}</p>}
    </div>
  );

  return (
    <div>
      {label}
      <input type={f.type||'text'} style={base} value={value||''} onChange={e => onChange(e.target.value)} placeholder={f.placeholder} />
      {error && <p style={styles.fieldError}>{error}</p>}
    </div>
  );
}

// ── Sign section ──────────────────────────────────────────────────────────────
function SignSection({ esignName, setEsignName, invite, error, submitting, onBack, onSubmit, declarationTexts, agreementUrl, agreementName }) {
  return (
    <div>
      <h3 style={styles.sectionTitle}>Declaration & Signature</h3>

      {agreementUrl && (
        <AgreementViewer agreementUrl={agreementUrl} displayName={agreementName} />
      )}

      {declarationTexts && declarationTexts.length > 0 && (
        <div style={{ marginBottom:20, padding:16, background:'#f8fafc',
          border:'1px solid #e2e8f0', borderRadius:8 }}>
          <p style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
            letterSpacing:'0.05em', color:'#64748b', margin:'0 0 12px' }}>Declaration</p>
          {declarationTexts.map((text, i) => (
            <p key={i} style={{ fontSize:13, color:'#374151', margin:'0 0 10px',
              lineHeight:1.7, paddingBottom:10,
              borderBottom: i < declarationTexts.length-1 ? '1px solid #e2e8f0' : 'none' }}>
              {text}
            </p>
          ))}
        </div>
      )}

      <p style={{ fontSize:13, color:'#4b5a6e', marginBottom:16 }}>
        By typing your full name below you confirm you have read the agreement document
        {agreementUrl ? ' above' : ''} and are electronically signing it.
        This signature is legally binding under the Electronic Communications Act 2000.
      </p>
      <label style={styles.label}>Full Name (Electronic Signature) <span style={{color:'#ef4444'}}>*</span></label>
      <input
        style={inp({ fontSize:16, fontStyle:'italic', color:'#1a2e4a' })}
        placeholder="Type your full name"
        value={esignName}
        onChange={e => setEsignName(e.target.value)}
      />
      {error && <p style={styles.error}>{error}</p>}
      <div style={styles.buttons}>
        <button onClick={onBack} style={styles.cancel}>← Back</button>
        <button onClick={onSubmit} disabled={submitting} style={{ ...styles.submit, opacity: submitting ? 0.7 : 1 }}>
          {submitting ? 'Submitting...' : '✓ Sign & Submit'}
        </button>
      </div>
    </div>
  );
}

// ── ID section ────────────────────────────────────────────────────────────────
function IDSection({ drivingLicence, setDrivingLicence, passport, setPassport, idSkipped, setIdSkipped, onBack, onNext }) {
  return (
    <div>
      <h3 style={styles.sectionTitle}>Identity Verification</h3>
      <p style={styles.sub}>Upload a copy of your driving licence and/or passport. You can skip this and provide it later.</p>
      <div style={{ marginBottom:14 }}>
        <label style={styles.label}>Driving Licence</label>
        <input type="file" accept="image/*,.pdf" onChange={e => setDrivingLicence(e.target.files[0])} style={{ fontSize:13 }} />
      </div>
      <div style={{ marginBottom:14 }}>
        <label style={styles.label}>Passport</label>
        <input type="file" accept="image/*,.pdf" onChange={e => setPassport(e.target.files[0])} style={{ fontSize:13 }} />
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
        <input type="checkbox" id="skip" checked={idSkipped} onChange={e => setIdSkipped(e.target.checked)} />
        <label htmlFor="skip" style={{ fontSize:13, color:'#374151' }}>I'll provide ID documents later</label>
      </div>
      <div style={styles.buttons}>
        <button onClick={onBack} style={styles.cancel}>← Back</button>
        <button onClick={onNext} style={styles.submit}>Next →</button>
      </div>
    </div>
  );
}

const styles = {
  page:        { minHeight:'100vh', background:'#F7FAFC', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'40px 16px' },
  card:        { background:'#fff', borderRadius:16, padding:32, width:'100%', maxWidth:620, boxShadow:'0 4px 24px rgba(0,0,0,0.08)' },
  header:      { marginBottom:24, borderBottom:'1px solid #f0f0f0', paddingBottom:16 },
  title:       { margin:'0 0 4px', fontSize:22, fontWeight:700, color:'#0e1b2c' },
  sub:         { color:'#718096', fontSize:13, margin:'0 0 16px' },
  sectionTitle:{ fontSize:17, fontWeight:700, color:'#0e1b2c', margin:'0 0 16px' },
  label:       { display:'block', fontSize:13, fontWeight:600, color:'#374151', marginBottom:4 },
  steps:       { display:'flex', gap:8, marginBottom:24 },
  step:        { width:32, height:32, borderRadius:'50%', background:'#e2e8f0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:600, color:'#718096' },
  stepActive:  { background:'#10b981', color:'#fff' },
  stepDone:    { background:'#d1fae5', color:'#10b981' },
  buttons:     { display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 },
  cancel:      { padding:'10px 20px', borderRadius:8, border:'1px solid #d1d5db', background:'#fff', cursor:'pointer', fontSize:14 },
  submit:      { padding:'10px 20px', borderRadius:8, border:'none', background:'#10b981', color:'#fff', cursor:'pointer', fontSize:14, fontWeight:600 },
  error:       { color:'#dc2626', fontSize:13, margin:'0 0 12px' },
  fieldError:  { color:'#ef4444', fontSize:11, margin:'-8px 0 8px' },
  center:      { minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' },
};
